import Vue from 'vue';
import { Component, Prop } from 'vue-property-decorator';
import { namespace } from 'vuex-class';

import { ObjectType, PopularTitlesEdge, SimilarTitlesEdge, TitleFilter } from '@/@types/graphql-types';
import {
	GetQualityTvSimilarTitlesDocument,
	GetSimilarTitlesDocument,
	GetSimilarTitlesQueryVariables,
	GetSimilarUpcomingTitlesDocument,
	GetSimilarUpcomingTitlesQueryVariables,
	SimilarTitleFragment,
} from '@/components/graphql/queries/GetSimilarTitles.query';
import { captureMessageForSentry } from '../sentry-helper';
import { TitleOfferFragment } from '@/components/buybox/graphql/fragments/Offer.fragment';

const Language = namespace('language');
const User = namespace('user');

@Component({
	name: 'SimilarTitlesProvider',
	apollo: {
		similarTitles: {
			query() {
				// APPLE_QUALITY_TV_EXP
				if (this.appleQualityTv) return GetQualityTvSimilarTitlesDocument;
				// APPLE_QUALITY_TV_EXP
				return this.isUpcoming ? GetSimilarUpcomingTitlesDocument : GetSimilarTitlesDocument;
			},
			variables(): GetSimilarTitlesQueryVariables | GetSimilarUpcomingTitlesQueryVariables {
				return this.queryVariables;
			},
			update(data) {
				if (this.isUpcoming) {
					return data?.newTitles?.edges.map((edge: PopularTitlesEdge) => edge.node) ?? [];
				}

				return data?.node?.similarTitlesV2?.edges.map((edge: SimilarTitlesEdge) => edge.node) ?? [];
			},
			errorPolicy: 'all',
			error(error) {
				captureMessageForSentry(
					'[GraphQL Get Similar Titles Data]:',
					{ error: error, where: 'Component: SimilarTitlesProvider' },
					'error'
				);
			},
		},
	},
})
export default class SimilarTitlesProvider extends Vue {
	@Prop({ required: true }) titleId: string;
	@Prop({ required: true }) titleObjectType: ObjectType;
	@Prop({ default: null }) upcomingFilters: Pick<TitleFilter, 'isUpcoming' | 'genres' | 'objectTypes'>;
	@Prop({ default: () => ({}) }) similarTitlesFilters: TitleFilter;
	@Prop({ default: 1 }) minTitles: number; // minimum number of titles to display the module
	// APPLE_QUALITY_TV_EXP
	@Prop({ default: false }) appleQualityTv: boolean;
	// APPLE_QUALITY_TV_EXP

	@Language.Getter country: string;
	@Language.Getter language: string;

	@User.Getter isPremium: boolean;

	isSeoSRLoaded = false;
	similarTitles: (SimilarTitleFragment & { offers: TitleOfferFragment[] })[] = [];

	get appleTitles() {
		return this.similarTitles.filter(title => title.offers && title.offers?.length > 0).slice(0, 8);
	}

	get isUpcoming() {
		return !!this.upcomingFilters;
	}

	get isEmpty() {
		return this.similarTitles?.length < this.minTitles;
	}

	get queryVariables() {
		const variables = {
			language: this.language,
			country: this.country,
			filters: {
				excludeIrrelevantTitles: this.isPremium,
				...this.similarTitlesFilters,
			},
		};

		// titleId is only required for SimilarTitles query, not SimilarUpcomingTitles
		if (this.isUpcoming) {
			variables.filters = { ...variables.filters, ...this.upcomingFilters };
			return variables as GetSimilarUpcomingTitlesQueryVariables;
		}

		(<GetSimilarTitlesQueryVariables>variables).titleId = this.titleId;

		return <GetSimilarTitlesQueryVariables>variables;
	}

	render() {
		if (this.$scopedSlots.default) {
			return this.$scopedSlots.default({
				titles: this.similarTitles,
				isEmpty: this.isEmpty,
				appleTitles: this.appleTitles,
			});
		}

		return this.$slots[0];
	}
}
